import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { makeCurrencyMask, makeTwoDecimalPointsNumberMask } from '@app/functions';
import { AdditionalPayItem } from '@app/models/payroll/additional-pay-item.model';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { HourlyPayItem } from '@app/models/payroll/hourly-pay-item.model';
import { Payroll } from '@app/models/payroll/payroll.model';
import { Paystub } from '@app/models/payroll/paystub.model';
import { SalaryPayItem } from '@app/models/payroll/salary-pay-item.model';
import {
    InsurableDollarPayGrouping,
    InsurableHourlyPayGrouping,
} from '@app/modules/payroll/interfaces/insurable-hours-pay-item-group.interface';
import { AuthService } from '@app/services';
import { formatLocalizedNumber, Locale, parseLocalizedNumber } from '@app/types/translatable.type';
import { BaseForm } from '@forms/base.form';
import { TranslateService } from '@ngx-translate/core';
import { flatten } from 'lodash-es';

@Component({
    selector: 'app-payroll-insurable-hours-form[paystub][payroll][insurableHoursAdditionalPayItemsTotal]',
    styleUrls: ['./insurable-hours-form.style.scss'],
    templateUrl: './insurable-hours-form.template.html',
})
export class InsurableHoursForm extends BaseForm implements OnInit, OnChanges {
    @Input() paystub!: Paystub;
    @Input() payroll!: Payroll;
    @Input() insurableHoursAdditionalPayItemsTotal!: string;
    @Input() displayInsurableHoursErrorString = false;
    @Input() insurableHoursErrorString = '';
    @Input() allowRecalculation = true;
    activeCompanyAdditionalIncomes: CompanyAdditionalIncome[] = [];
    isLoading = true;
    insurableHourlyPayItems: InsurableHourlyPayGrouping[] = [];
    insurableDollarPayItems: InsurableDollarPayGrouping[] = [];
    hasActiveIncomeTypes = false;
    currencyMask = makeCurrencyMask({}, this.translateService.currentLang as Locale);
    twoDecimalPointsNumberMask = makeTwoDecimalPointsNumberMask(this.translateService.currentLang as Locale);
    remainingVacationPayBalance = 0;
    frequencyOptions = {
        'One-off': 'forms.insurable-hours-form.frequency.one-off',
        Recurring: 'forms.insurable-hours-form.frequency.recurring',
    };

    constructor(
        private auth: AuthService,
        private translateService: TranslateService
    ) {
        super();
    }

    /**
     * Get a localized display value for a pay item amount
     * @param item - The pay item to get the formatted amount for, or a direct number value
     * @returns Formatted number string based on current locale
     */
    getFormattedAmount(item: AdditionalPayItem | number, decimalPlaces: number = 2): string | undefined {
        const amount = typeof item === 'number' ? item : item.amount;
        return formatLocalizedNumber(amount, {
            locale: this.translateService.currentLang as Locale,
            decimalPlaces,
        });
    }

    /**
     * Set a localized value for a pay item amount
     * @param item - The pay item to set the amount for
     * @param value - The localized string value to parse
     */
    setFormattedAmount(item: AdditionalPayItem, value: string): void {
        const parsedValue = parseLocalizedNumber(value, this.translateService.currentLang as Locale);
        item.amount = parsedValue ?? 0;
        this.updateInsurableHoursAdditionalPayItemsTotal(this.insurableHourlyPayItems, this.insurableDollarPayItems);
        this.updateVacationPayBalance(this.paystub, item, this.insurableHourlyPayItems, this.insurableDollarPayItems);
    }

    /**
     * Get a localized display value for a pay item hours
     * @param item - The pay item to get the formatted hours for, or a direct number value
     * @returns Formatted number string based on current locale
     */
    getFormattedHours(item: AdditionalPayItem | number): string | undefined {
        const hours = typeof item === 'number' ? item : item.hours;
        return formatLocalizedNumber(hours, {
            locale: this.translateService.currentLang as Locale,
            decimalPlaces: 0,
        });
    }

    /**
     * Set a localized value for a pay item hours
     * @param item - The pay item to set the hours for
     * @param value - The localized string value to parse
     * @param insurableHoursPayItemGroup - The group containing the pay item
     */
    setFormattedHours(
        item: AdditionalPayItem,
        value: string,
        insurableHoursPayItemGroup: InsurableHourlyPayGrouping
    ): void {
        const parsedValue = parseLocalizedNumber(value, this.translateService.currentLang as Locale);
        item.hours = parsedValue ?? 0;
        this.updatePayItemSubtotal(this.insurableHourlyPayItems, insurableHoursPayItemGroup, item);
        this.updateInsurableHoursAdditionalPayItemsTotal(this.insurableHourlyPayItems, this.insurableDollarPayItems);
        this.updateVacationPayBalance(this.paystub, item, this.insurableHourlyPayItems, this.insurableDollarPayItems);
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = false;
        const [companyAdditionalIncomes] = await CompanyAdditionalIncome.param('company', this.auth.company.id)
            .where('active', true)
            .where('required', false)
            .all();
        this.activeCompanyAdditionalIncomes = companyAdditionalIncomes;
        this.hasActiveIncomeTypes = this.activeCompanyAdditionalIncomes.length > 0;
    }

    async reloadCompanyAdditionalIncomes(): Promise<void> {
        const [companyAdditionalIncomes] = await CompanyAdditionalIncome.param('company', this.auth.company.id)
            .where('active', true)
            .where('required', false)
            .all();
        this.activeCompanyAdditionalIncomes = companyAdditionalIncomes;
    }

    ngOnChanges(): void {
        if (!this.paystub) {
            return;
        }
        if (this.activeCompanyAdditionalIncomes.length && this.allowRecalculation) {
            const activeCompanyAdditionalIncomesWithHours = this.activeCompanyAdditionalIncomes.filter(
                (activeCompanyAdditionalIncome: CompanyAdditionalIncome) => activeCompanyAdditionalIncome.hasHours
            );
            const activeCompanyAdditionalIncomesWithoutHours = this.activeCompanyAdditionalIncomes.filter(
                (activeCompanyAdditionalIncome: CompanyAdditionalIncome) => !activeCompanyAdditionalIncome.hasHours
            );

            // CAI will be used for shorthand of CompanyAdditionalIncome
            // CAI.has_hours === true which also means recurrable is false due to payroll validation
            this.insurableHourlyPayItems = this.buildInsurableHourlyPayItems(
                activeCompanyAdditionalIncomesWithHours,
                this.paystub
            );
            // CAI.has_hours === false CAI.recurrable
            this.insurableDollarPayItems = this.buildInsurableDollarPayItems(
                activeCompanyAdditionalIncomesWithoutHours,
                this.paystub
            );

            this.remainingVacationPayBalance = this.calculateVacationPayBalance(
                this.paystub,
                this.insurableHourlyPayItems,
                this.insurableDollarPayItems
            );
            this.updateInsurableHoursAdditionalPayItemsTotal(
                this.insurableHourlyPayItems,
                this.insurableDollarPayItems
            );
        }
    }

    calculateVacationPayBalance(
        paystub: Paystub,
        insurableHourPayItems: InsurableHourlyPayGrouping[],
        insurableDollarPayItems: InsurableDollarPayGrouping[]
    ): number {
        const paystubWorkingVacationPayBalance = paystub.workingVacationPayBalance;
        const allHourlyReduceVacationBalanceAmountsSummed: number = flatten(
            insurableHourPayItems.map((insurableHourlyPayItemGroup: InsurableHourlyPayGrouping) => {
                return insurableHourlyPayItemGroup.payItems
                    .filter(
                        (insurableHourlyPayItemGroupPayItem: AdditionalPayItem) =>
                            insurableHourlyPayItemGroupPayItem.reduceVacationPayBalance
                    )
                    .map(
                        (insurableHourlyPayItemGroupPayItem: AdditionalPayItem) =>
                            insurableHourlyPayItemGroupPayItem.amount
                    );
            })
        ).reduce((hoursAmountsSummed, amount) => hoursAmountsSummed + amount, 0);
        const allRecurrableReduceVacationBalanceDollarAmountsSummed: number = insurableDollarPayItems
            .filter((payItemGroup: InsurableDollarPayGrouping) => payItemGroup.payItem.reduceVacationPayBalance)
            .map((payItemGroup: InsurableDollarPayGrouping) => payItemGroup.payItem.amount)
            .reduce((dollarAmountsSummed, amount) => dollarAmountsSummed + amount, 0);

        const allAmountsSummed =
            allHourlyReduceVacationBalanceAmountsSummed + allRecurrableReduceVacationBalanceDollarAmountsSummed;

        return paystubWorkingVacationPayBalance - allAmountsSummed;
    }

    stripCurrencySymbolsToNumber(val: string | number): number {
        if (val) {
            this.resetCursorPosition();
        }
        return Number(val.toString().replace(/[^0-9.-]+/g, ''));
    }

    updatePaystubAdditionalPayItems(
        paystub: Paystub,
        insurableHoursPayItemGroupings: InsurableHourlyPayGrouping[]
    ): AdditionalPayItem[] {
        const insurableHoursPayItems = flatten(
            insurableHoursPayItemGroupings.map((grouping: InsurableHourlyPayGrouping) =>
                grouping.payItems.map((payItem: AdditionalPayItem) => payItem)
            )
        );
        return [...paystub.additionalPayItems, ...insurableHoursPayItems];
    }

    updatePayItemSubtotal(
        insurableHourlyPayItems: InsurableHourlyPayGrouping[],
        insurableHoursPayItemGroup: InsurableHourlyPayGrouping,
        passedPayItem: AdditionalPayItem
    ): void {
        const insurableGroupingIndex = insurableHourlyPayItems.findIndex(
            (insurableHourlyPayItemGroup: InsurableHourlyPayGrouping) => {
                return insurableHourlyPayItemGroup.name === insurableHoursPayItemGroup.name;
            }
        );
        const insurableGroupingPayItemIndex = insurableHourlyPayItems[insurableGroupingIndex].payItems.findIndex(
            (payItem: AdditionalPayItem) => {
                return payItem.compensationId === passedPayItem.compensationId;
            }
        );
        const calculatedPayItemSubtotal =
            insurableHourlyPayItems[insurableGroupingIndex].payItems[insurableGroupingPayItemIndex].hours *
            insurableHourlyPayItems[insurableGroupingIndex].payItems[insurableGroupingPayItemIndex].rateMultiplied;
        const roundedCalculatedPayItemSubtotal = Math.round(calculatedPayItemSubtotal * 100) / 100;

        this.insurableHourlyPayItems[insurableGroupingIndex].payItems[insurableGroupingPayItemIndex].amount =
            roundedCalculatedPayItemSubtotal;
    }

    updateInsurableHoursAdditionalPayItemsTotal(
        insurableHourlyPayItems: InsurableHourlyPayGrouping[],
        insurableDollarPayItems: InsurableDollarPayGrouping[]
    ): void {
        const allHourlyAmountsSummed: number = flatten(
            insurableHourlyPayItems.map((insurableHourlyPayItemGroup: InsurableHourlyPayGrouping) => {
                return insurableHourlyPayItemGroup.payItems.map(
                    (insurableHourlyPayItemGroupPayItem: AdditionalPayItem) => insurableHourlyPayItemGroupPayItem.amount
                );
            })
        ).reduce((hoursAmountsSummed, amount) => hoursAmountsSummed + amount, 0);

        const allDollarAmountsSummed: number = insurableDollarPayItems
            .map((payItemGroup: InsurableDollarPayGrouping) => payItemGroup.payItem.amount)
            .reduce((dollarAmountsSummed, amount) => dollarAmountsSummed + amount, 0);

        const allAmountsSummed = allHourlyAmountsSummed + allDollarAmountsSummed;

        this.insurableHoursAdditionalPayItemsTotal = allAmountsSummed.toFixed(2);
    }

    updateVacationPayBalance(
        paystub: Paystub,
        payItem: AdditionalPayItem,
        insurableHourlyPayItems: InsurableHourlyPayGrouping[],
        insurableDollarPayItems: InsurableDollarPayGrouping[]
    ): void {
        if (!payItem.reduceVacationPayBalance) {
            return;
        }
        this.remainingVacationPayBalance = this.calculateVacationPayBalance(
            paystub,
            insurableHourlyPayItems,
            insurableDollarPayItems
        );
    }

    private buildInsurableHourlyPayItems(
        activeCompanyAdditionalIncomes: CompanyAdditionalIncome[],
        paystub: Paystub
    ): InsurableHourlyPayGrouping[] {
        if (!activeCompanyAdditionalIncomes) {
            return [];
        }
        const employeePayPeriodPrimaryComps: (HourlyPayItem | SalaryPayItem)[] = !paystub.hourlyEmployee
            ? paystub.salaryPayItems
            : paystub.hourlyPayItems.filter((hourlyPayItem) => hourlyPayItem.canAddOvertime);

        const payPeriodInsurableHourlyPayGroupings: InsurableHourlyPayGrouping[] = activeCompanyAdditionalIncomes.map(
            (companyAdditionalIncome: CompanyAdditionalIncome) => {
                const name = companyAdditionalIncome.displayName;
                const additionalIncomesAndPrimaryHourlyRate = employeePayPeriodPrimaryComps.map(
                    (primaryComp: HourlyPayItem | SalaryPayItem) => {
                        const payItem = this.createPayItem(companyAdditionalIncome, primaryComp, paystub);
                        const rate = primaryComp.rate;
                        return { payItem, rate };
                    }
                );
                const payItems = additionalIncomesAndPrimaryHourlyRate.map((payGrouping) => payGrouping.payItem);
                return { name, payItems };
            }
        );
        return payPeriodInsurableHourlyPayGroupings;
    }

    private buildInsurableDollarPayItems(
        activeCompanyAdditionalIncomes: CompanyAdditionalIncome[],
        paystub: Paystub
    ): InsurableDollarPayGrouping[] {
        if (!activeCompanyAdditionalIncomes) {
            return [];
        }

        const companyAdditionalIncomesAndExistsOnPaystubStatus: {
            companyAdditionalIncome: CompanyAdditionalIncome;
            payItemExists: boolean;
        }[] = activeCompanyAdditionalIncomes.map((companyAdditionalIncome) => {
            const payItemExists = paystub.additionalPayItems.some(({ name }) => name === companyAdditionalIncome.name);
            return { companyAdditionalIncome, payItemExists };
        });

        const payPeriodInsurableDollarPayGroupings: InsurableDollarPayGrouping[] =
            companyAdditionalIncomesAndExistsOnPaystubStatus.map(
                ({
                    companyAdditionalIncome,
                }: {
                    companyAdditionalIncome: CompanyAdditionalIncome;
                    payItemExists: boolean;
                }) => {
                    const payItemExitsOnPaystubIncludes: boolean = paystub.included.some(
                        (includedItem: any) =>
                            includedItem.type === 'additional_pay_items' &&
                            includedItem.attributes?.name === companyAdditionalIncome.name &&
                            parseInt(includedItem.relationships?.paystub?.data?.id) === paystub.id
                    );
                    const payItemAsOnPaystubIncludes: any = paystub.included.find(
                        (includedItem: any) =>
                            includedItem.type === 'additional_pay_items' &&
                            includedItem.attributes?.name === companyAdditionalIncome.name &&
                            parseInt(includedItem.relationships?.paystub?.data?.id) === paystub.id
                    );
                    const frequency = payItemExitsOnPaystubIncludes
                        ? payItemAsOnPaystubIncludes.attributes.frequency
                        : 'One-off';
                    const payItem = this.createDollarPayItem(companyAdditionalIncome, paystub);
                    return { frequency, payItem };
                }
            );
        return payPeriodInsurableDollarPayGroupings;
    }

    private createPayItem(
        companyAdditionalIncome: CompanyAdditionalIncome,
        compensation: HourlyPayItem | SalaryPayItem,
        paystub: Paystub
    ): AdditionalPayItem {
        const exactPaystubAdditionalPayItem = paystub.additionalPayItems.find(
            (additionalPayItem) =>
                additionalPayItem.name === companyAdditionalIncome.name &&
                additionalPayItem.compensationId === compensation.compensationId
        ) ?? { amount: 0, hours: 0, id: null, employeeHoursExists: false };
        const payItem = new AdditionalPayItem({
            name: companyAdditionalIncome.name,
            displayName: companyAdditionalIncome.displayName,
            paystubId: paystub.id,
            amount: exactPaystubAdditionalPayItem.amount,
            hours: exactPaystubAdditionalPayItem.hours,
            employeeHoursExists: exactPaystubAdditionalPayItem.employeeHoursExists,
            editable: true,
            userModified: true,
            compensationId: compensation.compensationId,
            rate: paystub?.hourlyEmployee ? compensation.rate : compensation.hourlyRate,
            multiplier: companyAdditionalIncome.multiplier,
            id: exactPaystubAdditionalPayItem.id,
            subtractFromSalary: companyAdditionalIncome.subtractFromSalary,
            ...(companyAdditionalIncome.reduceVacationPayBalance && {
                reduceVacationPayBalance: companyAdditionalIncome.reduceVacationPayBalance,
            }),
        });
        return payItem;
    }

    private getPaystubAdditionalPayItemProps(
        companyAdditionalIncome: CompanyAdditionalIncome,
        paystub: Paystub
    ): { amount: number; id: number | null } {
        const exactPaystubAdditionalPayItem = paystub.additionalPayItems.find(
            (additionalPayItem) => additionalPayItem.name === companyAdditionalIncome.name
        ) ?? { amount: 0, id: null };
        const { amount, id } = exactPaystubAdditionalPayItem;
        return { amount, id };
    }

    private createDollarPayItem(companyAdditionalIncome: CompanyAdditionalIncome, paystub: Paystub): AdditionalPayItem {
        const displayName = companyAdditionalIncome.displayName;
        const payItem = new AdditionalPayItem({
            name: companyAdditionalIncome.name,
            displayName,
            paystubId: paystub.id,
            amount: this.getPaystubAdditionalPayItemProps(companyAdditionalIncome, paystub).amount,
            hours: 0,
            editable: true,
            userModified: true,
            id: this.getPaystubAdditionalPayItemProps(companyAdditionalIncome, paystub).id,
            ...(companyAdditionalIncome.reduceVacationPayBalance && {
                reduceVacationPayBalance: companyAdditionalIncome.reduceVacationPayBalance,
            }),
        });
        return payItem;
    }

    private resetCursorPosition(): void {
        const focusedInput = <HTMLInputElement>document.activeElement;
        const selectionStart = focusedInput.selectionStart;
        setTimeout(() => {
            focusedInput.selectionStart = selectionStart;
            focusedInput.selectionEnd = selectionStart;
        });
    }
}
